// RSO input week: display rules for the calendar the forecasting service decides.
// The browser never decides which month is open; it only formats the server's window.
// Every date here is a Denver calendar date ("YYYY-MM-DD") or month ("YYYY-MM-01" / "YYYY-MM").

export const DENVER_TIME_ZONE = "America/Denver";
// These refusals mean "this month can't be written now". They never block navigation or drafts.
export const WINDOW_CODES = ["forecast_month_not_open", "month_closed", "window_unavailable"];

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August",
  "September", "October", "November", "December"];
const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const isRecord = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const count = (value) => Number.isSafeInteger(value) && value >= 0;
const plural = (value, one, many) => `${value} ${value === 1 ? one : many}`;

export const isWindowCode = (code) => WINDOW_CODES.includes(code);
export const windowCodeOf = (failure) => {
  const code = failure?.response?.data?.code;
  return isWindowCode(code) ? code : null;
};

// "2026-10-01" and "2026-10" both mean October 2026.
export const monthKey = (value) => {
  const match = /^(\d{4})-(0[1-9]|1[0-2])(?:-01)?$/.exec(String(value ?? "").trim());
  return match && match[1] !== "0000" ? `${match[1]}-${match[2]}` : "";
};
export const monthDate = (value) => (monthKey(value) ? `${monthKey(value)}-01` : "");
export const monthName = (value) => (monthKey(value) ? MONTHS[Number(monthKey(value).slice(5, 7)) - 1] : "");
export const monthYear = (value) => (monthKey(value) ? `${monthName(value)} ${monthKey(value).slice(0, 4)}` : "");
export const addMonths = (value, months) => {
  const key = monthKey(value);
  if (!key) return "";
  const index = Number(key.slice(0, 4)) * 12 + Number(key.slice(5, 7)) - 1 + months;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, "0")}`;
};

const parseDay = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? ""));
  if (!match) return null;
  const day = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return day.getUTCFullYear() === Number(match[1]) && day.getUTCMonth() === Number(match[2]) - 1
    && day.getUTCDate() === Number(match[3]) ? day : null;
};
export const isCalendarDate = (value) => parseDay(value) !== null;
export const addDays = (value, days) => {
  const day = parseDay(value);
  if (!day) return "";
  day.setUTCDate(day.getUTCDate() + days);
  return day.toISOString().slice(0, 10);
};
// "Wed Sep 30"
export const shortDate = (value) => {
  const day = parseDay(value);
  return day ? `${WEEKDAYS[day.getUTCDay()]} ${SHORT_MONTHS[day.getUTCMonth()]} ${day.getUTCDate()}` : "";
};
// "Sep 30"
export const monthDay = (value) => {
  const day = parseDay(value);
  return day ? `${SHORT_MONTHS[day.getUTCMonth()]} ${day.getUTCDate()}` : "";
};
// A month's input week as NANCY resolved it, from the window (its input month) or from
// GET Dashboard/api/RsoInputWindow (any month): admins can set a month's dates. Else null.
// A window may also list the resolved weeks around today (inputWindows: [{inputMonth, opensOn, closesOn}]).
const knownWeek = (source, key) => {
  if (!isRecord(source)) return null;
  if (source.enforced === true && Array.isArray(source.inputWindows)) {
    const listed = source.inputWindows.map((span) => knownWeek(span, key)).find(Boolean);
    if (listed) return listed;
  }
  if (monthKey(source.inputMonth) !== key) return null;
  const [start, end] = source.enforced === true ? [source.inputWeekStart, source.inputWeekEnd] : [source.opensOn, source.closesOn];
  return isCalendarDate(start) && isCalendarDate(end) && start <= end ? {start, end} : null;
};
// A month is entered during its input week: the dates NANCY resolved when they are known
// (pass the window and/or RsoInputWindow reads), else the standard rule, the last 7 days of the month before it.
export const inputWeekFor = (month, ...known) => {
  const key = monthKey(month);
  if (!key) return null;
  for (const source of known) {
    const week = knownWeek(source, key);
    if (week) return week;
  }
  const end = addDays(`${key}-01`, -1);
  return {start: addDays(end, -6), end};
};

const validInstant = (value) => typeof value === "string" && !Number.isNaN(new Date(value).getTime());
const zoneParts = (instant, timeZone) => {
  const parts = new Intl.DateTimeFormat("en-US", {timeZone, weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true}).formatToParts(instant);
  const part = (type) => parts.find((item) => item.type === type)?.value || "";
  return `${part("weekday")} ${part("month")} ${part("day")}, ${part("hour")}:${part("minute")} ${part("dayPeriod").toUpperCase()}`;
};
export const browserTimeZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch (_) { return ""; }
};
// For browsers outside Mountain Time (the India QA team): " (Thu Oct 1, 11:29 AM your time)".
export const localTimeHint = (instant, localTimeZone = browserTimeZone()) => {
  if (instant == null) return "";
  const at = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(at.getTime()) || !localTimeZone) return "";
  try {
    const local = zoneParts(at, localTimeZone);
    return local !== zoneParts(at, DENVER_TIME_ZONE) ? ` (${local} your time)` : "";
  } catch (_) { return ""; }
};
// The UTC instant of 00:00 Mountain Time on a calendar date (MDT is UTC-6, MST is UTC-7).
const denverMidnight = (value) => {
  const day = parseDay(value);
  if (!day) return null;
  for (const offset of [6, 7]) {
    const candidate = new Date(day.getTime() + offset * 3600000);
    const parts = new Intl.DateTimeFormat("en-US", {timeZone: DENVER_TIME_ZONE, hourCycle: "h23",
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit"}).formatToParts(candidate);
    const part = (type) => parts.find((item) => item.type === type)?.value;
    if (`${part("year")}-${part("month")}-${part("day")}` === value && Number(part("hour")) === 0) return candidate;
  }
  return null;
};
// "Sep 28" for an instant, as a Mountain Time calendar date.
export const denverMonthDay = (instant) => {
  if (!validInstant(instant)) return "";
  const parts = new Intl.DateTimeFormat("en-US", {timeZone: DENVER_TIME_ZONE, month: "short", day: "numeric"})
    .formatToParts(new Date(instant));
  const part = (type) => parts.find((item) => item.type === type)?.value || "";
  return `${part("month")} ${part("day")}`;
};

// GET Dashboard/api/RsoPropertyForecast/window → {window, canViewTeamStatus}.
// Anything other than a complete, enforced window means "switched off": today's screens.
export const normalizeEntryWindow = (value) => {
  if (!isRecord(value) || value.enforced !== true) return null;
  const months = ["currentMonth", "inputMonth", "changeMonth"];
  const dates = ["today", "inputWeekStart", "inputWeekEnd", "nextInputWeekStart"];
  const monthLists = ["writableMonths", "viewableMonths", "sendableChangeMonths"];
  if (months.some((field) => !monthKey(value[field])) || dates.some((field) => !isCalendarDate(value[field]))
    || monthLists.some((field) => !Array.isArray(value[field]) || value[field].some((month) => !monthKey(month)))
    || !validInstant(value.closesAtUtc) || typeof value.inputWeekOpen !== "boolean"
    || (value.closedByAdmin != null && typeof value.closedByAdmin !== "boolean")
    || (value.firstEnforcedMonth != null && !monthKey(value.firstEnforcedMonth))
    || !value.viewableMonths.length) return null;
  return {...value, closedByAdmin: value.closedByAdmin === true, firstEnforcedMonth: value.firstEnforcedMonth ?? null};
};
// windowUnavailable: the input week is switched on but NANCY couldn't reach the forecasting service.
// overrideEnabled: NANCY and the forecasting service both allow sending outside the input week with a sign-off.
export const normalizeWindowResponse = (data) => {
  const entryWindow = normalizeEntryWindow(isRecord(data) ? data.window : null);
  return {window: entryWindow, canViewTeamStatus: !!entryWindow && data.canViewTeamStatus === true,
    windowUnavailable: !entryWindow && isRecord(data) && data.windowUnavailable === true,
    overrideEnabled: !!entryWindow && data.overrideEnabled === true};
};
export const normalizeTotals = (value) => isRecord(value) && ["required", "done", "left", "unsentChanges"]
  .every((field) => count(value[field])) ? value : null;

const monthsOf = (list) => (Array.isArray(list) ? list.map(monthKey).filter(Boolean) : []);
// changeMonth is the input month only during the input week (calendar rule; an early close never moves it).
// An admin may set the current month's week later in the month: then the input month is the current month
// (so is changeMonth) before its week opens, and inputWeekOpen tells the two apart.
export const isInputWeek = (entryWindow) => !!entryWindow && monthKey(entryWindow.changeMonth) === monthKey(entryWindow.inputMonth)
  && (entryWindow.inputWeekOpen !== false || entryWindow.closedByAdmin === true);
export const isWritableMonth = (entryWindow, month) => !entryWindow || monthsOf(entryWindow.writableMonths).includes(monthKey(month));
export const isViewableMonth = (entryWindow, month) => !entryWindow || monthsOf(entryWindow.viewableMonths).includes(monthKey(month));
export const canSendChangeMonth = (entryWindow, month) => !entryWindow || monthsOf(entryWindow.sendableChangeMonths).includes(monthKey(month));
export const viewableMonths = (entryWindow) => monthsOf(entryWindow?.viewableMonths);
// The month the checklist opens on: the one being entered (or the next one to be entered).
export const defaultWindowMonth = (entryWindow) => monthsOf(entryWindow?.writableMonths)[0] || monthKey(entryWindow?.inputMonth);
// The month whose answers the dashboard tile summarizes: the input month during its week; otherwise the month whose
// week ended last (lastClosedMonth: an admin may close next month's week before that month starts), when Sales can
// still view it, else the current month (the standard rule: both are the current month).
export const tileMonth = (entryWindow) => {
  if (isInputWeek(entryWindow)) return monthKey(entryWindow.inputMonth);
  const lastClosed = monthKey(entryWindow?.lastClosedMonth);
  return lastClosed && viewableMonths(entryWindow).includes(lastClosed) ? lastClosed : monthKey(entryWindow?.currentMonth);
};

const lastMinuteBefore = (instant) => (instant ? new Date(instant.getTime() - 60000) : null);
// "Wed Sep 30, 11:59 PM Mountain Time"
export const deadlineText = (entryWindow, {localTimeZone} = {}) => {
  const hint = localTimeHint(lastMinuteBefore(new Date(entryWindow.closesAtUtc)), localTimeZone);
  return `${shortDate(entryWindow.inputWeekEnd)}, 11:59 PM Mountain Time${hint}`;
};
export const isDueToday = (entryWindow) => !!entryWindow?.inputWeekOpen && entryWindow.today === entryWindow.inputWeekEnd;
// The month whose input week ended last and the day it ended: as the window reports them (lastClosedMonth,
// lastClosedOn), else the current month's week as NANCY resolved it (the window's inputWindows, or known: a
// GET RsoInputWindow read), else the standard rule (the day before the current month began).
export const lastClosedWeek = (entryWindow, known) => {
  if (monthKey(entryWindow?.lastClosedMonth) && isCalendarDate(entryWindow?.lastClosedOn)) {
    return {month: monthKey(entryWindow.lastClosedMonth), end: entryWindow.lastClosedOn};
  }
  const month = monthKey(entryWindow?.currentMonth);
  const end = inputWeekFor(month, entryWindow, known)?.end || "";
  return month && end ? {month, end} : null;
};
export const endedDate = (entryWindow, known) => lastClosedWeek(entryWindow, known)?.end || "";
const opensText = (entryWindow) => {
  const next = isInputWeek(entryWindow) ? addMonths(entryWindow.inputMonth, 1) : monthKey(entryWindow.inputMonth);
  return `${monthName(next)} forecasts open ${shortDate(entryWindow.nextInputWeekStart)}.`;
};

// "Forecast for October 2026 · input week Sep 24 – Sep 30 (Mountain Time)"
export const checklistHeader = (month, ...known) => {
  const week = inputWeekFor(month, ...known);
  return week ? {month: monthYear(month), week: `input week ${monthDay(week.start)} – ${monthDay(week.end)} (Mountain Time)`} : null;
};
// "October: 9 of 12 done · due Wed Sep 30, 11:59 PM Mountain Time"
export const checklistProgress = (entryWindow, totals, month, options) => {
  if (!entryWindow || !normalizeTotals(totals)) return "";
  const progress = `${monthName(month)}: ${totals.done} of ${totals.required} done`;
  return isWritableMonth(entryWindow, month) ? `${progress} · due ${deadlineText(entryWindow, options)}` : progress;
};
// Checklist / Switch property: drafts for the month on screen that Purchasing hasn't got. "" when there are none.
// "Draft for October 2026 not sent to Purchasing yet: Oak Ridge." Up to 3 names, then "A, B and N more".
export const unsentDraftsNotice = (names, month) => {
  const list = (Array.isArray(names) ? names : []).filter((name) => typeof name === "string" && name.trim()).map((name) => name.trim());
  if (!list.length || !monthKey(month)) return "";
  const shown = list.length > 3 ? `${list.slice(0, 2).join(", ")} and ${list.length - 2} more` : list.join(", ");
  return `${list.length === 1 ? "Draft" : `${list.length} drafts`} for ${monthYear(month)} not sent to Purchasing yet: ${shown}.`;
};

// Shown above a month that can be viewed but not changed.
// known: a GET RsoInputWindow read for the current month (its week may have been set by an admin).
export const readOnlyBanner = (entryWindow, month, {localTimeZone, known} = {}) => {
  if (!entryWindow || isWritableMonth(entryWindow, month)) return "";
  if (isInputWeek(entryWindow) && entryWindow.closedByAdmin) {
    return `${monthYear(entryWindow.inputMonth)} entry was closed early. Ask your sales manager if something still needs to be entered. ${opensText(entryWindow)}`;
  }
  if (isInputWeek(entryWindow)) {
    return `${monthYear(month)} is view only. Only ${monthYear(entryWindow.inputMonth)} can be entered this week.`;
  }
  const last = lastClosedWeek(entryWindow, known);
  const ended = last?.end || "";
  const hint = localTimeHint(lastMinuteBefore(denverMidnight(addDays(ended, 1))), localTimeZone);
  return `The ${monthName(last?.month || entryWindow.currentMonth)} input week ended ${shortDate(ended)}, 11:59 PM Mountain Time${hint}. ${opensText(entryWindow)}`;
};

// A send or review refused because the month closed. The page keeps every value on screen.
// A month's entry closes at 11:59 PM Mountain Time on the last day of its input week (closedOn when the
// caller knows NANCY's date for it, else the standard rule: the last day of the month before it).
export const windowRefusalMessage = (code, month, serverMessage, {localTimeZone, closedOn} = {}) => {
  if (code === "forecast_month_not_open") {
    const closed = isCalendarDate(closedOn) ? closedOn : addDays(monthDate(month), -1);
    const hint = localTimeHint(lastMinuteBefore(denverMidnight(addDays(closed, 1))), localTimeZone);
    return `${monthName(month)} entry closed at 11:59 PM Mountain Time${hint}. Your changes are still on this screen.`;
  }
  if (code === "month_closed") return `${monthYear(month)} entry was closed early. Ask your sales manager if something still needs to be entered. Your changes are still on this screen.`;
  if (code === "window_unavailable") return `${serverMessage || "We couldn't confirm the RSO input week. Try again in a minute."} Your changes are still on this screen.`;
  return "";
};

// Override: Sales may send a month outside the input window, but only while the week is not open (outside the
// last 7 days, or the input month was closed early), and only this month (late) or next month (early).
// Every override carries a sign-off; the forecasting service applies the same rule again and records it.
// When the window lists its overrideMonths (admins can move a window), those are the months.
export const overrideMonths = (entryWindow) => (entryWindow && entryWindow.inputWeekOpen === false
  ? Array.isArray(entryWindow.overrideMonths) ? monthsOf(entryWindow.overrideMonths)
    : [monthKey(entryWindow.currentMonth), monthKey(entryWindow.inputMonth)].filter(Boolean) : []);
export const canOverrideMonth = (entryWindow, month) => !!monthKey(month) && overrideMonths(entryWindow).includes(monthKey(month));
// Refusals of an override send. They come with the server's own message.
export const OVERRIDE_CODES = ["override_signoff_required", "override_signoff_invalid", "override_not_needed", "override_not_enabled"];
export const overrideCodeOf = (failure) => {
  const code = failure?.response?.data?.code;
  return OVERRIDE_CODES.includes(code) ? code : null;
};
export const overrideNotNeededMessage = "The input week is open now, so this doesn't need an override. Review and send it normally.";
// A refused override shows the server's own reason ("entry closed" would be wrong for next month sent early).
export const overrideRefusalMessage = (code, month, serverMessage) => {
  if (code === "override_not_needed") return overrideNotNeededMessage;
  const message = typeof serverMessage === "string" && serverMessage.trim() ? serverMessage.trim() : "";
  if (OVERRIDE_CODES.includes(code)) return message || "This override can't be sent now. Your changes are still on this screen.";
  if (!isWindowCode(code) || code === "window_unavailable") return windowRefusalMessage(code, month, serverMessage);
  const reason = message || `${monthName(month)} can't be entered outside the input window now.`;
  return `${/[.!?]$/.test(reason) ? reason : `${reason}.`} Your changes are still on this screen.`;
};
// Why the month is outside the window, as the forecasting service records it: this month is late, next month is
// early, or next month after its entry was closed early.
export const overrideReasonOf = (entryWindow, month) => {
  const key = monthKey(month);
  if (!entryWindow || !key) return "";
  if (key === monthKey(entryWindow.currentMonth)) return "late";
  // A month before the input month whose window has closed (when the window lists override months) is late too.
  if (key < monthKey(entryWindow.inputMonth) && key > monthKey(entryWindow.currentMonth) && overrideMonths(entryWindow).includes(key)) return "late";
  if (key !== monthKey(entryWindow.inputMonth)) return "";
  return isInputWeek(entryWindow) && entryWindow.closedByAdmin ? "closed_early" : "early";
};
const OVERRIDE_REASON_WORDS = {late: "late", early: "early", closed_early: "after entry closed early"};
// The words for a recorded reason, else "": only the map's own keys count, so "toString" is not a reason.
export const overrideReasonWords = (reason) =>
  (Object.prototype.hasOwnProperty.call(OVERRIDE_REASON_WORDS, reason) ? OVERRIDE_REASON_WORDS[reason] : "");
const SIGN_OFF_NOTE_LIMIT = 2000;
// Control characters other than line breaks and tabs (the forecasting service refuses them).
const CONTROL_CHARACTERS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/; // eslint-disable-line no-control-regex
// The sign-off as it is sent: the date Sales told Purchasing and the note, trimmed.
export const signOffBody = (signOff) => ({toldPurchasingOn: String(signOff?.toldPurchasingOn ?? ""),
  note: String(signOff?.note ?? "").trim()});
// The rules any screen can check without a clock. The forecasting service also checks the date against its own today.
export const validSignOff = (signOff) => {
  const {toldPurchasingOn, note} = signOffBody(signOff);
  return isCalendarDate(toldPurchasingOn) && note.length >= 1 && note.length <= SIGN_OFF_NOTE_LIMIT
    && !CONTROL_CHARACTERS.test(note);
};
const materialName = (name) => (typeof name === "string" && name.trim() ? name.trim() : "RSO material");
// "Iowa 6 turns"
export const materialTurnsText = (name, turns) => `${materialName(name)}${count(turns) ? ` ${plural(turns, "turn", "turns")}` : ""}`;
// "Iowa 4 → 6 turns", "Iowa (not sent before) → 6 turns"
export const turnsChangeText = (name, before, after) => (count(after)
  ? `${materialName(name)} ${count(before) ? before : "(not sent before)"} → ${plural(after, "turn", "turns")}`
  : `${materialTurnsText(name, before)} → not sent`);
// The line above an override's sign-off: a first answer for the month, or a change to what Purchasing already has.
// before: what this page last read ({revision, noUnits, legacy, entries}); after: what is being sent ({noUnits, entries}).
// The forecasting service decides the recorded case itself; this line is for Sales only.
export const overrideCaseLine = ({month, reason, before, after}) => {
  const sentBefore = (Array.isArray(before?.entries) ? before.entries : []).filter((entry) => count(entry?.unitsTurning));
  const sending = (Array.isArray(after?.entries) ? after.entries : []).filter((entry) => count(entry?.unitsTurning));
  if (Number(before?.revision) === 0 && before?.legacy !== true) {
    return `First answer for ${monthName(month)}, sent ${overrideReasonWords(reason) || "outside the input window"}${after?.noUnits ? ": No units this month" : ""}`;
  }
  const had = before?.noUnits === true ? "No units" : before?.legacy === true ? "the floor-plan forecast"
    : sentBefore.filter((entry) => entry.unitsTurning > 0).map((entry) => materialTurnsText(entry.productName, entry.unitsTurning)).join(", ")
      || "every material at 0";
  let change;
  if (after?.noUnits) change = before?.noUnits === true ? "No units again" : `${had} → No units`;
  else if (before?.noUnits === true || before?.legacy === true) {
    change = `${had} → ${sending.map((entry) => materialTurnsText(entry.productName, entry.unitsTurning)).join(", ")}`;
  } else {
    change = sending.map((entry) => turnsChangeText(entry.productName,
      sentBefore.find((sent) => Number(sent.productId) === Number(entry.productId))?.unitsTurning, entry.unitsTurning)).join("; ");
  }
  return `Change to what Purchasing has: ${change}`;
};

// Why a saved material change can't be sent today (the caller shows Discard either way).
// A change for a later month can still go out during that month's input week.
export const unsendableChangeNote = (entryWindow, month, fromName) => {
  const key = monthKey(month);
  if (!entryWindow || !key) return "";
  if (key >= monthKey(entryWindow.inputMonth)) {
    const week = inputWeekFor(key, entryWindow);
    return `You can send this change during the ${monthName(key)} input week (${shortDate(week.start)} – ${shortDate(week.end)}, Mountain Time), or discard it now.`;
  }
  if (key < monthKey(entryWindow.currentMonth)) {
    const previous = typeof fromName === "string" && fromName.trim() ? fromName.trim() : "the previous material";
    return `This change can no longer be sent. Discard it to restore ${previous}, then make the change again if it is still needed.`;
  }
  return "";
};

// Checklist row labels (plan §6.3). The server decides the state; the browser only names it.
const STATE_LABELS = {
  change_unsent: "Material change saved, press Send",
  covered_by_change: "Covered by material change",
  stock: "Stock material, nothing to enter",
  assignment_syncing: "Recently assigned: you can enter it after the next sync",
  previous_owner: "Sent by previous owner, please resend",
  units_sent: "Sent to Purchasing",
  legacy: "Sent to Purchasing (floor-plan forecast)",
  no_units: "No units this month",
  all_zero: "All materials set to 0: confirm No units or enter units",
  setup_problem: "Can't calculate units: setup problem",
  draft: "Draft saved, not sent",
  not_started: "Not started",
};
export const CHECKLIST_STATES = Object.keys(STATE_LABELS);
export const checklistStateLabel = (property) => {
  const state = property?.state;
  if (!STATE_LABELS[state]) return "";
  const turns = property.coveredUnitsTurning;
  if (state === "covered_by_change" && count(turns)) return `Covered by material change (${plural(turns, "unit turn", "unit turns")})`;
  if (["units_sent", "no_units", "legacy"].includes(state) && property.savedBeforeInputWeek === true) {
    const saved = denverMonthDay(property.answerSavedAt);
    return saved ? `Sent ${saved}, before the input week: check it` : "Sent before the input week: check it";
  }
  return STATE_LABELS[state];
};

// Checklist card (window on only): what went to Purchasing for the viewed month, and when.
// row.monthActivity comes from NANCY's workspace; without it (an older API) or a known state the card stays as today.
const SENT_STATES = ["units_sent", "no_units", "legacy", "all_zero", "covered_by_change", "previous_owner"];
const FORECAST_ANSWERS = ["units", "no_units", "all_zero", "legacy"];
const CHANGE_STATUSES = ["sent", "unsent", "withdrawn"];
const text = (value) => (typeof value === "string" && value.trim() ? value.trim() : "");
const turnsText = (value) => (count(value) ? plural(value, "unit turn", "unit turns") : "");
const instantOf = (value) => (validInstant(value) ? new Date(value).getTime() : null);
const dated = (label, instant) => (denverMonthDay(instant) ? `${label} ${denverMonthDay(instant)}` : label);
const materialLine = (material) => [text(material.name) || "RSO material", turnsText(material.unitsTurning)].join(" · ");
const changeNames = (change) => (text(change.fromName) || text(change.toName)
  ? `${text(change.fromName) || "Previous material"} → ${text(change.toName) || "new material"}` : "");
const changeLine = (change) => [changeNames(change) || "Material change", turnsText(change.unitsTurning)].filter(Boolean).join(" · ");
const forecastOf = (value) => (isRecord(value) && FORECAST_ANSWERS.includes(value.answer) ? {answer: value.answer, savedAt: value.savedAt,
  materials: Array.isArray(value.materials) ? value.materials.filter((material) => isRecord(material)
    && count(material.unitsTurning) && material.unitsTurning > 0) : []} : null);
const forecastSummary = (forecast, month) => {
  if (forecast.answer === "no_units") return `No units for ${monthName(month)}`;
  if (forecast.answer === "all_zero") return "every material at 0";
  if (forecast.answer === "legacy") return "floor-plan forecast";
  if (forecast.materials.length === 1) return materialLine(forecast.materials[0]);
  return forecast.materials.length ? `units for ${forecast.materials.length} materials` : "unit turns";
};
export const checklistCard = (row, month) => {
  if (!isRecord(row) || !STATE_LABELS[row.state] || !isRecord(row.monthActivity)) return null;
  const activity = row.monthActivity;
  const name = monthName(month);
  const forecast = forecastOf(activity.forecast);
  const changes = (Array.isArray(activity.changes) ? activity.changes : [])
    .filter((change) => isRecord(change) && CHANGE_STATUSES.includes(change.status));
  const [sent, unsent, withdrawn] = CHANGE_STATUSES.map((status) => changes.filter((change) => change.status === status));
  const answeredAt = validInstant(forecast?.savedAt) ? forecast.savedAt : row.answerSavedAt;
  const sentOn = denverMonthDay(answeredAt) ? ` ${denverMonthDay(answeredAt)}` : "";
  const beforeWeek = row.savedBeforeInputWeek === true;
  const line = (value, material = false) => ({text: value, material});
  const materials = forecast?.answer === "units" ? forecast.materials.map((item) => line(materialLine(item), true)) : [];
  const forecastLines = (label) => (forecast ? [line(`${dated(label, answeredAt)}: ${forecastSummary(forecast, month)}`)] : []);
  const changeLines = (label, list) => list.map((change) => line(`${dated(label, change.sentAt)}: ${changeLine(change)}`));
  let headline = STATE_LABELS[row.state];
  let lines = [];
  if (row.state === "units_sent") {
    headline = beforeWeek ? checklistStateLabel(row) : `Units sent to Purchasing${sentOn}`;
    lines = materials;
  } else if (row.state === "no_units") {
    headline = beforeWeek ? checklistStateLabel(row) : `No units for ${name} · sent to Purchasing${sentOn}`;
    lines = beforeWeek ? [line(`No units for ${name}`)] : [];
  } else if (row.state === "legacy") {
    headline = beforeWeek ? checklistStateLabel(row) : `Sent to Purchasing${sentOn} (floor-plan forecast)`;
    lines = beforeWeek ? [line("Floor-plan forecast")] : [];
  } else if (row.state === "all_zero") {
    headline = `Sent${sentOn} with every material at 0`;
    lines = [line(`Choose No units for ${name}, or enter units.`)];
  } else if (row.state === "previous_owner") {
    headline = `Sent${sentOn} by the previous salesperson: please resend`;
    lines = forecast?.answer === "no_units" ? [line(`No units for ${name}`)] : materials;
  } else if (row.state === "covered_by_change") {
    headline = dated("Material change sent to Purchasing", sent[0]?.sentAt);
    lines = [...sent.slice(0, 1).map((change) => line(changeLine(change))), ...forecastLines("Also sent"), ...changeLines("Also sent", sent.slice(1))];
  } else if (row.state === "change_unsent") {
    headline = "Material change not sent yet: press Send";
    const notSent = unsent.map((change) => line(`Not sent: ${changeLine(change)}${denverMonthDay(change.savedAt) ? ` (saved ${denverMonthDay(change.savedAt)})` : ""}`));
    const alreadySent = [...forecastLines("Already sent"), ...changeLines("Already sent", sent)];
    lines = [...notSent.slice(0, 1), ...(alreadySent.length ? alreadySent : [line(`Nothing sent to Purchasing for ${name} yet.`)]), ...notSent.slice(1)];
  } else if (row.state === "draft") {
    headline = `Nothing sent for ${name} yet`;
    lines = [line(denverMonthDay(activity.draftSavedAt) ? `Draft saved ${denverMonthDay(activity.draftSavedAt)}, not sent` : "Draft saved, not sent")];
  } else if (row.state === "not_started") {
    headline = `Nothing sent for ${name} yet`;
  } else if (row.state === "assignment_syncing") {
    lines = [line(forecast ? `Last sent${sentOn}` : `Nothing sent for ${name} yet.`)];
  } else if (row.state === "setup_problem") {
    lines = [line(`Nothing sent for ${name} yet. You can still choose No units.`)];
  }
  // A draft saved after the last send on a done property, then any withdrawn material change.
  const lastSent = Math.max(-Infinity, ...[answeredAt, ...sent.map((change) => change.sentAt)].map(instantOf).filter((value) => value !== null));
  if (SENT_STATES.includes(row.state) && instantOf(activity.draftSavedAt) !== null && instantOf(activity.draftSavedAt) > lastSent) {
    lines = [...lines, line(`Newer draft saved ${denverMonthDay(activity.draftSavedAt)}, not sent`)];
  }
  lines = [...lines, ...withdrawn.map((change) => line(`${dated("Material change withdrawn", change.withdrawnAt)}${changeNames(change) ? `: ${changeNames(change)}` : ""}`))];
  if (lines.length <= 2) return {headline, details: lines.map((item) => item.text)};
  const hidden = lines.slice(1);
  return {headline, details: hidden.every((item) => item.material)
    ? [lines[0].text, `+${plural(hidden.length, "more material", "more materials")}: open the property to see them`]
    : [lines[0].text, `${lines[1].text} · +${lines.length - 2} more: open the property`]};
};

// Checklist row (window on only): the same facts as checklistCard, shaped for a compact row.
// tone colors the status tag; items are what went to Purchasing (or what's waiting), at most 3.
// Returns null when monthActivity or a known state is missing, so the row stays as today.
const ROW_STATUS = {
  units_sent: ["sent", "Units sent"],
  no_units: ["none", "No units"],
  legacy: ["sent", "Sent (floor-plan forecast)"],
  covered_by_change: ["change", "Material change sent"],
  all_zero: ["todo", "All materials at 0"],
  previous_owner: ["attention", "Resend needed"],
  change_unsent: ["attention", "Change not sent"],
  draft: ["todo", "Draft saved"],
  not_started: ["todo", "Nothing sent yet"],
  assignment_syncing: ["neutral", "Recently assigned"],
  setup_problem: ["attention", "Setup problem"],
  stock: ["neutral", "Stock material"],
};
export const checklistRow = (row, month) => {
  if (!isRecord(row) || !ROW_STATUS[row.state] || !isRecord(row.monthActivity)) return null;
  const activity = row.monthActivity;
  const name = monthName(month);
  const forecast = forecastOf(activity.forecast);
  const changes = (Array.isArray(activity.changes) ? activity.changes : [])
    .filter((change) => isRecord(change) && CHANGE_STATUSES.includes(change.status));
  const [sent, unsent, withdrawn] = CHANGE_STATUSES.map((status) => changes.filter((change) => change.status === status));
  const answeredAt = validInstant(forecast?.savedAt) ? forecast.savedAt : row.answerSavedAt;
  const [tone, label] = ROW_STATUS[row.state];
  const materials = forecast?.answer === "units"
    ? forecast.materials.map((item) => ({kind: "material", name: text(item.name) || "RSO material", turns: item.unitsTurning})) : [];
  const change = (item, note = "") => ({kind: "change", from: text(item.fromName) || "Previous material",
    to: text(item.toName) || "new material", turns: count(item.unitsTurning) ? item.unitsTurning : null, note});
  const note = (value) => ({kind: "note", text: value});
  const forecastItems = (prefix) => (!forecast ? [] : forecast.answer === "units" && materials.length
    ? materials.map((item) => ({...item, note: prefix})) : [note(`${prefix}: ${forecastSummary(forecast, month)}`)]);
  let status = {tone, label, date: ""};
  let items = [];
  if (row.state === "units_sent" || row.state === "legacy") {
    status.date = denverMonthDay(answeredAt);
    items = materials;
  } else if (row.state === "no_units") {
    status.date = denverMonthDay(answeredAt);
    items = [note(`No RSO units needed in ${name}`)];
  } else if (row.state === "all_zero") {
    status.date = denverMonthDay(answeredAt);
    items = [note(`Choose No units for ${name}, or enter units.`)];
  } else if (row.state === "previous_owner") {
    status.date = denverMonthDay(answeredAt);
    items = [note("Sent by the previous salesperson. Check it and send it again."), ...materials];
  } else if (row.state === "covered_by_change") {
    status.date = denverMonthDay(sent[0]?.sentAt);
    items = [...sent.slice(0, 1).map((item) => change(item)), ...forecastItems(dated("Also sent", answeredAt)),
      ...sent.slice(1).map((item) => change(item, dated("Also sent", item.sentAt)))];
  } else if (row.state === "change_unsent") {
    items = [...unsent.map((item) => change(item, "Saved, not sent: press Send")),
      ...forecastItems(dated("Already sent", answeredAt)), ...sent.map((item) => change(item, dated("Already sent", item.sentAt)))];
  } else if (row.state === "draft") {
    status.date = denverMonthDay(activity.draftSavedAt);
    items = [note(`Not sent to Purchasing yet`)];
  } else if (row.state === "assignment_syncing") {
    items = [note("You can enter it after the next sync.")];
  } else if (row.state === "setup_problem") {
    items = [note(`Can't calculate units. You can still choose No units for ${name}.`)];
  } else if (row.state === "stock") {
    items = [note("Nothing to enter")];
  }
  // An answer sent before this month's input week still counts, but Sales should check it.
  if (["units_sent", "no_units", "legacy"].includes(row.state) && row.savedBeforeInputWeek === true) {
    status = {tone: "todo", label: "Sent early: check it", date: status.date};
  }
  const lastSent = Math.max(-Infinity, ...[answeredAt, ...sent.map((item) => item.sentAt)].map(instantOf).filter((value) => value !== null));
  if (SENT_STATES.includes(row.state) && instantOf(activity.draftSavedAt) !== null && instantOf(activity.draftSavedAt) > lastSent) {
    items = [...items, note(`Newer draft saved ${denverMonthDay(activity.draftSavedAt)}, not sent`)];
  }
  items = [...items, ...withdrawn.map((item) => ({...change(item, dated("Withdrawn", item.withdrawnAt)), withdrawn: true}))];
  if (items.length > 3) items = [...items.slice(0, 2), note(`+${items.length - 2} more: open the property to see them`)];
  return {...status, items};
};

// Dashboard tile (plan §4). Returns the plain sentences shown under the RSO tile.
export const tileSummary = (entryWindow, totals, options = {}) => {
  if (!entryWindow) return null;
  const valid = normalizeTotals(totals);
  const month = tileMonth(entryWindow);
  const unsent = valid?.unsentChanges > 0
    ? `${plural(valid.unsentChanges, "material change", "material changes")} not sent to Purchasing` : "";
  if (entryWindow.inputWeekOpen) {
    const due = isDueToday(entryWindow)
      ? `Due today, 11:59 PM Mountain Time${localTimeHint(lastMinuteBefore(new Date(entryWindow.closesAtUtc)), options.localTimeZone)}.`
      : `Due ${deadlineText(entryWindow, options)}.`;
    if (!valid) return {month, tone: isDueToday(entryWindow) ? "due-today" : "open", headline: `${monthName(month)} RSO forecast`, detail: due, unsent: ""};
    if (valid.required === 0) return {month, tone: "none", headline: `You have no RSO properties to forecast for ${monthName(month)}.`, detail: "", unsent};
    if (valid.left === 0 && valid.unsentChanges === 0) {
      return {month, tone: "complete", headline: `${monthName(month)} forecast complete.`,
        detail: `You can change answers until ${deadlineText(entryWindow, options)}.`, unsent: ""};
    }
    return {month, tone: isDueToday(entryWindow) ? "due-today" : "open",
      headline: `${monthName(month)} RSO forecast: ${valid.left} of ${valid.required} left.`, detail: due, unsent};
  }
  const enforced = !entryWindow.firstEnforcedMonth || month >= monthKey(entryWindow.firstEnforcedMonth);
  // A month whose week hasn't happened yet (an admin set the current month's week later in the month) hasn't closed.
  const closed = !monthKey(entryWindow.lastClosedMonth) || month <= monthKey(entryWindow.lastClosedMonth);
  const missing = valid && valid.left > 0 && enforced && closed
    ? `${monthName(month)} closed with ${plural(valid.left, "property", "properties")} unanswered. Your sales manager can see this list.` : "";
  return {month, tone: missing ? "closed-missing" : "closed", headline: missing, detail: opensText(entryWindow), unsent};
};
